/**
 * Revenue Target Settings
 *
 * Inline editor for the target assumptions: quarter revenue target,
 * net margin goal, monthly AI budget and the reporting calendar (the
 * month the financial year starts, which sets the fiscal quarters).
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { FormEvent } from 'react';
import { formatCurrency } from '@/utils/formatters';
import { DEFAULT_FISCAL_YEAR_START_MONTH, MONTH_NAMES, quarterEndLabel } from '@/utils/fiscalCalendar';
import type {
  RevenueTargetConfig,
  RevenueTargetConfigInput,
} from '@/types/revenueTarget';

interface RevenueTargetSettingsProps {
  config: RevenueTargetConfig;
  requiredRevenueForMargin: number;
  saving: boolean;
  saveError: string | null;
  onSave: (input: RevenueTargetConfigInput) => Promise<boolean>;
}

export function RevenueTargetSettings({
  config,
  requiredRevenueForMargin,
  saving,
  saveError,
  onSave,
}: RevenueTargetSettingsProps) {
  const [targetRevenue, setTargetRevenue] = useState('');
  const [marginPercent, setMarginPercent] = useState('');
  const [aiBudget, setAiBudget] = useState('');
  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(
    String(DEFAULT_FISCAL_YEAR_START_MONTH)
  );
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState(false);

  // Only re-seed the form when the persisted config actually changed
  // (updatedAt moved) — a background snapshot refresh must not wipe
  // the user's in-progress edits.
  const lastAppliedRef = useRef<string>('');
  useEffect(() => {
    if (config.updatedAt === lastAppliedRef.current) return;
    lastAppliedRef.current = config.updatedAt;
    setTargetRevenue(
      config.targetRevenue !== null ? String(config.targetRevenue) : ''
    );
    setMarginPercent(String(config.targetNetMarginPercent));
    setAiBudget(String(config.aiBudgetMonthly));
    setFiscalYearStartMonth(
      String(config.fiscalYearStartMonth ?? DEFAULT_FISCAL_YEAR_START_MONTH)
    );
    setNotes(config.notes ?? '');
  }, [config]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      setSaved(false);

      const input: RevenueTargetConfigInput = {
        targetRevenue: targetRevenue.trim() === ''
          ? null
          : Number(targetRevenue),
        targetNetMarginPercent: Number(marginPercent),
        aiBudgetMonthly: Number(aiBudget),
        fiscalYearStartMonth: Number(fiscalYearStartMonth),
        // No UI for this yet (API-set); preserve it across saves so a
        // pinned non-AI baseline is not silently dropped.
        subscriptionsBaselineMonthly:
          config.subscriptionsBaselineMonthly ?? null,
        notes: notes.trim() === '' ? undefined : notes.trim(),
      };

      const ok = await onSave(input);
      setSaved(ok);
    },
    [
      targetRevenue,
      marginPercent,
      aiBudget,
      fiscalYearStartMonth,
      notes,
      config.subscriptionsBaselineMonthly,
      onSave,
    ]
  );

  return (
    <section className="rt-section rt-settings" aria-label="Target settings">
      <h3 className="rt-section__title">Target settings</h3>
      <form onSubmit={handleSubmit} className="rt-settings__form">
        <div className="rt-settings__fields">
          <label className="rt-field">
            <span className="rt-field__label">Quarter revenue target ($)</span>
            <input
              type="number"
              min="1"
              max="100000000"
              step="1000"
              value={targetRevenue}
              onChange={(e) => setTargetRevenue(e.target.value)}
              placeholder={`Auto: ${formatCurrency(requiredRevenueForMargin)}`}
              className="rt-field__input"
            />
            <span className="rt-field__hint">
              Leave blank to auto-compute from the margin goal
            </span>
          </label>
          <label className="rt-field">
            <span className="rt-field__label">Net profit margin goal (%)</span>
            <input
              type="number"
              min="0"
              max="90"
              step="0.5"
              value={marginPercent}
              onChange={(e) => setMarginPercent(e.target.value)}
              required
              className="rt-field__input"
            />
          </label>
          <label className="rt-field">
            <span className="rt-field__label">AI budget per month ($)</span>
            <input
              type="number"
              min="0"
              max="10000000"
              step="500"
              value={aiBudget}
              onChange={(e) => setAiBudget(e.target.value)}
              required
              className="rt-field__input"
            />
          </label>
          <label className="rt-field">
            <span className="rt-field__label">Financial year starts in</span>
            <select
              value={fiscalYearStartMonth}
              onChange={(e) => setFiscalYearStartMonth(e.target.value)}
              className="rt-field__input"
            >
              {MONTH_NAMES.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
            <span className="rt-field__hint">
              Sets the reporting quarters — currently ending{' '}
              {quarterEndLabel(Number(fiscalYearStartMonth))}
            </span>
          </label>
        </div>
        <label className="rt-field rt-field--wide">
          <span className="rt-field__label">Notes</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value.replace(/[<>]/g, ''))}
            rows={2}
            maxLength={2000}
            className="rt-field__input rt-field__textarea"
            placeholder="Assumptions, commitments, context for this target"
          />
        </label>
        <div className="rt-settings__actions">
          <button type="submit" disabled={saving} className="rt-settings__save">
            {saving ? 'Saving...' : 'Save target'}
          </button>
          {saved && !saveError && (
            <span className="rt-settings__saved" role="status">
              Saved
            </span>
          )}
          {saveError && (
            <span className="rt-settings__error" role="alert">
              {saveError}
            </span>
          )}
        </div>
      </form>
    </section>
  );
}
